import React, { useMemo } from 'react';
import styles from './CampaignManagerInfluencerForm.module.css';
import { Field, FieldProps, Form, Formik, FormikHelpers, ErrorMessage } from 'formik';
import { DropdownOptionWithSubLabel, OptionWithSubLabel, FormField, Label, ModalTitle } from '@round/ui-kit';
import { CampaignManagerRetrieveInfluencer, CampaignManagerInfluencerApiBody, PaymentTerms } from '@round/api';
import cn from 'classnames';
import * as Yup from 'yup';
import { ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../../../utils/utils.types';
import { Select } from '@round/ui-kit';
import { useInfluencerTags } from '../../../../../hooks/useInfluencerTags';
import { ReactComponent as CloseIcon } from '../../../../../assets/CloseCircleIcon.svg';
import Badge from '../../../../../ui/Badge/Badge';
import useNonNullContext from '../../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../../contexts/OptionsContext';
import Button from '../../../../../ui/Button/Button';
import SectionTitle from '../../../../../ui/SectionTitle/SectionTitle';
import Checkbox from '../../../../../ui/DataEntry/Checkbox/Checkbox';
import { paymentTermOptions, preferredPaymentOptions } from '../../../../../utils/constants';
import TextField from '../../../../../ui/DataEntry/Formik/TextField';
import InfluencerCostField from '../../../../creator/components/InfluencerCostField/InfluencerCostField';
import { InfluencerCostFormFields } from '../../../../creator/creator.types';
import { useSelectPaginatedOptions } from '../../../../../hooks/useSelectPaginatedOptions';
import { getCampaignManagerApexCreatorAgencies } from '@round/api';
import { useContentTags } from '../../../../../contexts/ContentTagsContext';

export type CampaignManagerInfluencerFormValues = Partial<
    Omit<
        CampaignManagerInfluencerApiBody,
        | 'content_tags'
        | 'location'
        | 'payment_terms'
        | 'preferred_currency'
        | 'exclusivity'
        | 'preferred_payment_method'
        | 'roster_share_agency'
    > & {
        costs: InfluencerCostFormFields;
        content_tags: ValueType<GenericDropdownOption<number>, true>;
        location: GenericDropdownOption<number>;
        payment_terms: DropdownOptionWithSubLabel<PaymentTerms>;
        preferred_currency: GenericDropdownOption<number>;
        preferred_payment_method: GenericDropdownOption<CampaignManagerRetrieveInfluencer['preferred_payment_method']>;
        roster_share_agency: ValueType<GenericDropdownOption<number>, false>;
    }
>;

export type CampaignManagerInfluencerFormProps = {
    handleSubmit: (
        values: CampaignManagerInfluencerFormValues,
        helpers: FormikHelpers<CampaignManagerInfluencerFormValues>
    ) => void;
    initialValues: CampaignManagerInfluencerFormValues;
};

const validationSchema = Yup.object({
    name: Yup.string().required('this field cannot be empty'),
    email: Yup.string().email('Invalid email').required('This field cannot be empty'),
});

const CampaignManagerInfluencerForm = ({ initialValues, handleSubmit }: CampaignManagerInfluencerFormProps) => {
    const { influencerTags } = useInfluencerTags();
    const { tags: contentTags } = useContentTags();
    const { currencies } = useNonNullContext(OptionsContext);

    const contentTagOptions = useMemo(
        () =>
            contentTags.map((tag) => ({
                value: tag.id,
                label: tag.name,
            })),
        [contentTags]
    );

    const locationOptions = useMemo(
        () =>
            influencerTags
                .filter((tag) => tag.type === 'LOCATION')
                .map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                })),
        [influencerTags]
    );

    const currencyOptions = useMemo(
        () =>
            currencies.map((curr) => ({
                value: curr.id,
                label: curr.name,
            })),
        [currencies]
    );

    const { props: creatorAgencySelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getCampaignManagerApexCreatorAgencies
    >({
        fetchOptions: getCampaignManagerApexCreatorAgencies,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    return (
        <div className={styles.main}>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize
            >
                {(props) => {
                    const handleRemoveTag = (id: number) => {
                        props.setFieldValue(
                            'content_tags',
                            props.values.content_tags?.filter((tag) => tag.value !== id)
                        );
                    };
                    return (
                        <>
                            <Form className={styles.form}>
                                <div className={styles.headerRow}>
                                    <ModalTitle>Creator Details</ModalTitle>
                                    <div className={styles.headerActions}>
                                        <div className={styles.checkboxWrapper}>
                                            <Label htmlFor="is_available_for_booking">Is available for booking?:</Label>
                                            <Field name="is_available_for_booking">
                                                {(
                                                    props: FieldProps<
                                                        CampaignManagerInfluencerFormValues['is_available_for_booking'],
                                                        CampaignManagerInfluencerFormValues
                                                    >
                                                ) => (
                                                    <Checkbox
                                                        value={Boolean(props.field.value)}
                                                        onChange={(value) =>
                                                            props.form.setFieldValue(props.field.name, value)
                                                        }
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <Button
                                            className={styles.submitButton}
                                            onClick={props.submitForm}
                                            htmlType={'button'}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>

                                <SectionTitle>Social accounts</SectionTitle>
                                <div className={styles.sectionBody}>
                                    <div className={styles.row}>
                                        <TextField
                                            className={styles.field}
                                            name="tiktok_username"
                                            label="TikTok"
                                            placeholder="Enter TikTok username"
                                        />
                                        <TextField
                                            className={styles.field}
                                            name="instagram_username"
                                            label="Instagram"
                                            placeholder="Enter Instagram username"
                                        />
                                        <TextField
                                            className={styles.field}
                                            name="youtube_username"
                                            label="Shorts (YouTube)"
                                            placeholder="Enter YouTube username"
                                        />
                                    </div>
                                </div>

                                <SectionTitle>Creator details</SectionTitle>
                                <div className={styles.sectionBody}>
                                    <div className={styles.row}>
                                        <TextField
                                            className={styles.field}
                                            name="name"
                                            label="Display Name"
                                            placeholder="Enter display name"
                                        />
                                    </div>
                                    <div className={styles.row}>
                                        <TextField
                                            className={styles.field}
                                            name="first_name"
                                            label="First Name"
                                            placeholder="Enter first name"
                                        />
                                        <TextField
                                            className={styles.field}
                                            name="last_name"
                                            label="Last Name"
                                            placeholder="Enter last name"
                                        />
                                        <TextField
                                            className={styles.field}
                                            name="email"
                                            label="Email"
                                            placeholder="Enter email"
                                        />
                                    </div>
                                    <div className={styles.row}>
                                        <TextField
                                            className={styles.field}
                                            name="phone"
                                            label="Phone Number"
                                            placeholder="Enter phone number"
                                        />
                                        <TextField
                                            className={styles.field}
                                            name="birthday"
                                            label="Birthday"
                                            placeholder="dd/mm/yyyy"
                                        />
                                        <FormField className={styles.field}>
                                            <Label htmlFor="location" className={styles.label}>
                                                Location of creator
                                            </Label>
                                            <Field name="location" id="location">
                                                {(
                                                    props: FieldProps<CampaignManagerInfluencerFormValues['location']>
                                                ) => (
                                                    <Select
                                                        placeholder="Select country"
                                                        value={props.field.value}
                                                        isClearable={true}
                                                        onChange={(option) => {
                                                            props.form.setFieldValue(props.field.name, option);
                                                        }}
                                                        options={locationOptions}
                                                        menuPosition={'fixed'}
                                                        menuPortalTarget={document.body}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="location">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>

                                    <div className={styles.row}>
                                        <FormField className={styles.field}>
                                            <Label htmlFor="roster_share_agency">Roster Share</Label>
                                            <Field name="roster_share_agency" id="roster_share_agency">
                                                {(
                                                    props: FieldProps<
                                                        CampaignManagerInfluencerFormValues['roster_share_agency']
                                                    >
                                                ) => (
                                                    <Select
                                                        {...creatorAgencySelectProps}
                                                        isClearable
                                                        value={props.field.value}
                                                        placeholder="-"
                                                        onBlur={() => {
                                                            props.form.setFieldTouched(props.field.name, true);
                                                        }}
                                                        onChange={(value) => {
                                                            props.form.setFieldValue(props.field.name, value);
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </FormField>

                                        <TextField
                                            name="contact_details"
                                            label="Contact Details"
                                            placeholder="Enter contact details"
                                            className={styles.field}
                                        />
                                    </div>
                                </div>

                                <div className={styles.sectionBody}>
                                    <div className={cn(styles.row, styles.addressRow)}>
                                        <TextField
                                            className={styles.field}
                                            name="address_line_1"
                                            label="First Line of Address"
                                            placeholder="Enter first line of address"
                                        />
                                        <TextField
                                            className={cn(styles.field, styles.shortField)}
                                            name="address_city"
                                            label="City"
                                            placeholder="Enter city"
                                        />
                                    </div>
                                    <div className={cn(styles.row, styles.addressRow)}>
                                        <TextField
                                            className={styles.field}
                                            name="address_region"
                                            label="State / Region"
                                            placeholder="Enter State / Region"
                                        />
                                        <TextField
                                            className={cn(styles.field, styles.shortField)}
                                            name="address_postcode"
                                            label="Zip Code / Post Code"
                                            placeholder="Enter Zip Code / Post Code"
                                        />
                                    </div>
                                </div>

                                <SectionTitle>Prices</SectionTitle>
                                <div className={styles.sectionBody}>
                                    <div className={cn(styles.row, styles.priceColumnsRow)}>
                                        <div className={styles.column}>
                                            <div className={styles.costPlatformHeading}>TikTok</div>
                                            <div className={styles.costRow}>
                                                <div className={styles.costColumn}>
                                                    <h4 className={styles.costCampaignHeading}>AUDIO</h4>
                                                    <InfluencerCostField
                                                        name="costs.TIKTOK.AUDIO.USD"
                                                        currencySymbol="$"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.TIKTOK.AUDIO.GBP"
                                                        currencySymbol="£"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.TIKTOK.AUDIO.EUR"
                                                        currencySymbol="€"
                                                    />
                                                </div>
                                                <div className={styles.costColumn}>
                                                    <h4 className={styles.costCampaignHeading}>BRAND</h4>
                                                    <InfluencerCostField
                                                        name="costs.TIKTOK.BRAND.USD"
                                                        currencySymbol="$"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.TIKTOK.BRAND.GBP"
                                                        currencySymbol="£"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.TIKTOK.BRAND.EUR"
                                                        currencySymbol="€"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.column}>
                                            <div className={styles.costPlatformHeading}>Instagram</div>
                                            <div className={styles.costRow}>
                                                <div className={styles.costColumn}>
                                                    <h4 className={styles.costCampaignHeading}>AUDIO</h4>
                                                    <InfluencerCostField
                                                        name="costs.INSTAGRAM.AUDIO.USD"
                                                        currencySymbol="$"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.INSTAGRAM.AUDIO.GBP"
                                                        currencySymbol="£"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.INSTAGRAM.AUDIO.EUR"
                                                        currencySymbol="€"
                                                    />
                                                </div>
                                                <div className={styles.costColumn}>
                                                    <h4 className={styles.costCampaignHeading}>BRAND</h4>
                                                    <InfluencerCostField
                                                        name="costs.INSTAGRAM.BRAND.USD"
                                                        currencySymbol="$"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.INSTAGRAM.BRAND.GBP"
                                                        currencySymbol="£"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.INSTAGRAM.BRAND.EUR"
                                                        currencySymbol="€"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.column}>
                                            <div className={styles.costPlatformHeading}>Shorts (Youtube)</div>
                                            <div className={styles.costRow}>
                                                <div className={styles.costColumn}>
                                                    <h4 className={styles.costCampaignHeading}>AUDIO</h4>
                                                    <InfluencerCostField
                                                        name="costs.YOUTUBE.AUDIO.USD"
                                                        currencySymbol="$"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.YOUTUBE.AUDIO.GBP"
                                                        currencySymbol="£"
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.YOUTUBE.AUDIO.EUR"
                                                        currencySymbol="€"
                                                    />
                                                </div>
                                                <div className={styles.costColumn}>
                                                    <h4 className={styles.costCampaignHeading}>BRAND</h4>
                                                    <InfluencerCostField
                                                        name="costs.YOUTUBE.BRAND.USD"
                                                        currencySymbol={'$'}
                                                    />
                                                    <InfluencerCostField
                                                        name="costs.YOUTUBE.BRAND.GBP"
                                                        currencySymbol="£"
                                                    />
                                                    <InfluencerCostField
                                                        name={'costs.YOUTUBE.BRAND.EUR'}
                                                        currencySymbol="€"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <SectionTitle>Creator details</SectionTitle>
                                <div className={styles.sectionBody}>
                                    <div className={cn(styles.row, styles.tagsRow)}>
                                        <FormField className={styles.field}>
                                            <Label htmlFor="content_tags" className={styles.label}>
                                                Add content tags
                                            </Label>
                                            <Field name="content_tags" id="content_tags">
                                                {(
                                                    props: FieldProps<
                                                        CampaignManagerInfluencerFormValues['content_tags']
                                                    >
                                                ) => (
                                                    <Select
                                                        isMulti
                                                        placeholder="Select Tags"
                                                        value={props.field.value}
                                                        onChange={(option) => {
                                                            props.form.setFieldValue(props.field.name, option);
                                                        }}
                                                        options={contentTagOptions}
                                                        menuPosition={'fixed'}
                                                        menuPortalTarget={document.body}
                                                        controlShouldRenderValue={false}
                                                        components={{
                                                            ClearIndicator: () => null,
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="content_tags">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                        <div className={styles.tagsContainer}>
                                            {contentTags
                                                .filter(
                                                    (tag) =>
                                                        props.values.content_tags
                                                            ?.map((tagOption) => tagOption.value)
                                                            .includes(tag.id)
                                                )
                                                .map((tag) => (
                                                    <Badge className={styles.tag}>
                                                        <div>{tag.name}</div>
                                                        <CloseIcon
                                                            className={styles.removeTagIcon}
                                                            onClick={() => handleRemoveTag(tag.id)}
                                                        />
                                                    </Badge>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                <SectionTitle>Creator payment details</SectionTitle>
                                <div className={styles.sectionBody}>
                                    <div className={cn(styles.row, styles.bankDetailsRow)}>
                                        <TextField
                                            className={styles.field}
                                            name="bank_name"
                                            label="Bank Details - Full name on account"
                                            placeholder="Enter name on account"
                                        />
                                        <FormField className={cn(styles.field, styles.shortField)}>
                                            <Label htmlFor="payment_terms" className={styles.label}>
                                                Payment Terms
                                            </Label>
                                            <Field name="payment_terms" id="payment_terms">
                                                {(
                                                    props: FieldProps<
                                                        CampaignManagerInfluencerFormValues['payment_terms']
                                                    >
                                                ) => (
                                                    <Select
                                                        isSearchable={false}
                                                        placeholder="Select payment terms"
                                                        value={props.field.value}
                                                        onChange={(option) => {
                                                            props.form.setFieldValue(props.field.name, option);
                                                        }}
                                                        options={paymentTermOptions}
                                                        menuPosition={'fixed'}
                                                        menuPortalTarget={document.body}
                                                        components={{ Option: OptionWithSubLabel }}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="payment_terms">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>
                                    <div className={cn(styles.row, styles.bankDetailsRow)}>
                                        <TextField
                                            className={styles.field}
                                            name="bank_account_number"
                                            label="Bank Details - Account number"
                                            placeholder="Enter account number"
                                        />
                                        <FormField className={cn(styles.field, styles.shortField)}>
                                            <Label htmlFor="preferred_currency" className={styles.label}>
                                                Preferred Currency
                                            </Label>
                                            <Field name="preferred_currency" id="preferred_currency">
                                                {(
                                                    props: FieldProps<
                                                        CampaignManagerInfluencerFormValues['preferred_currency']
                                                    >
                                                ) => (
                                                    <Select
                                                        isSearchable={false}
                                                        placeholder="Select from options"
                                                        value={props.field.value}
                                                        onChange={(option) => {
                                                            props.form.setFieldValue(props.field.name, option);
                                                        }}
                                                        options={currencyOptions}
                                                        menuPosition={'fixed'}
                                                        menuPortalTarget={document.body}
                                                        isClearable={true}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="preferred_currency">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>
                                    <div className={cn(styles.row, styles.bankDetailsRow)}>
                                        <TextField
                                            className={styles.field}
                                            name="bank_sort_code"
                                            label="Bank Details - Sort code/Routing number"
                                            placeholder="Enter Sort code/Routing Number"
                                        />

                                        <FormField className={cn(styles.field, styles.shortField)}>
                                            <Label htmlFor="preferred_payment_method">Preferred Payment Method</Label>
                                            <Field name="preferred_payment_method">
                                                {(
                                                    props: FieldProps<
                                                        CampaignManagerInfluencerFormValues['preferred_payment_method'],
                                                        CampaignManagerInfluencerFormValues
                                                    >
                                                ) => (
                                                    <Select
                                                        isSearchable={false}
                                                        placeholder="Select from options"
                                                        menuPosition={'fixed'}
                                                        menuPortalTarget={document.body}
                                                        value={props.field.value}
                                                        onChange={(value) =>
                                                            props.form.setFieldValue(props.field.name, value)
                                                        }
                                                        options={preferredPaymentOptions}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="preferred_payment_method">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>

                                    <div className={cn(styles.row, styles.bankDetailsRow)}>
                                        <TextField
                                            className={styles.field}
                                            name="paypal_email_address"
                                            label="Bank Details - Paypal Email"
                                            placeholder="Enter paypal email"
                                        />

                                        <div className={cn(styles.field, styles.shortField)} />
                                    </div>
                                </div>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

export default CampaignManagerInfluencerForm;
